import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AlertCircle, Download, MessageSquarePlus, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { FileStatusBadge } from "@/components/files/FileStatusBadge";
import { downloadFile } from "@/components/files/FileRow";
import { useFile, useReprocessFile } from "@/hooks/queries";
import { api } from "@/lib/api";
import { FILE_KINDS, formatBytes, isProcessing, isReady } from "@/lib/files";
import { formatDateTime, formatNumber } from "@/lib/format";

function ImagePreview({ fileId }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    api.get(`/files/${fileId}/download?inline=true`).then((d) => setUrl(d.url)).catch(() => setUrl(null));
  }, [fileId]);
  if (!url) return <div className="h-48 animate-pulse rounded-xl bg-white/[0.04]" />;
  return <img src={url} alt="" className="max-h-72 w-full rounded-xl border border-white/[0.08] object-contain" data-testid="file-detail-image" />;
}

function Meta({ label, value }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-[0.14em] text-zinc-600">{label}</div>
      <div className="mt-1 text-sm text-zinc-200">{value}</div>
    </div>
  );
}

function Status({ file, onReprocess, busy }) {
  if (isProcessing(file)) return <p className="text-sm text-zinc-400" data-testid="file-detail-processing">ChatSleek is reading this file. This usually takes a few seconds.</p>;
  if (file.status === "failed")
    return (
      <div className="flex items-start gap-3 rounded-xl border border-rose-400/20 bg-rose-500/[0.05] p-3.5" data-testid="file-detail-error">
        <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-rose-300" />
        <div className="text-sm">
          <p className="text-zinc-200">{file.error_message || "ChatSleek couldn't read this file."}</p>
          <p className="mt-1 text-xs text-zinc-500">The file is still stored safely in your account.</p>
          <button type="button" onClick={onReprocess} disabled={busy} className="mt-3 inline-flex items-center gap-1.5 rounded-full border border-white/15 px-3 py-1.5 text-xs text-zinc-200 hover:bg-white/[0.05]" data-testid="file-detail-reprocess">
            <RotateCcw className="h-3.5 w-3.5" /> Try again
          </button>
        </div>
      </div>
    );
  if (!file.ai_readable) return <p className="text-sm text-zinc-400" data-testid="file-detail-not-readable">Stored securely. ChatSleek can't read the contents of images yet, so they can't be attached to chats.</p>;
  return null;
}

export function FileDetailSheet({ fileId, onClose }) {
  const navigate = useNavigate();
  const { data, isLoading, isError, error } = useFile(fileId);
  const reprocess = useReprocessFile();
  const file = data?.file;
  const retry = () => reprocess.mutateAsync(fileId).catch((e) => toast.error(e.message));
  return (
    <Sheet open={Boolean(fileId)} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full overflow-y-auto border-white/10 bg-ink-900 sm:max-w-lg" data-testid="file-detail-sheet">
        {isLoading && <div className="mt-10 h-6 w-2/3 animate-pulse rounded bg-white/[0.06]" />}
        {isError && <p className="mt-10 text-sm text-zinc-400" data-testid="file-detail-load-error">{error.message}</p>}
        {file && (
          <>
            <SheetHeader className="pr-6 text-left">
              <SheetTitle className="break-words text-lg font-semibold text-white" data-testid="file-detail-name">{file.name}</SheetTitle>
              <FileStatusBadge file={file} className="w-fit" />
            </SheetHeader>
            <div className="mt-6 grid grid-cols-2 gap-5">
              <Meta label="Type" value={FILE_KINDS[file.kind]?.label} />
              <Meta label="Size" value={formatBytes(file.size_bytes)} />
              <Meta label="Uploaded" value={formatDateTime(file.created_at)} />
              {file.page_count ? <Meta label={file.kind === "xlsx" ? "Sheets" : file.kind === "pptx" ? "Slides" : "Pages"} value={file.page_count} /> : null}
              {isReady(file) && <Meta label="Readable text" value={`${formatNumber(file.token_count)} tokens${file.truncated ? " (partial)" : ""}`} />}
            </div>
            <div className="mt-6"><Status file={file} onReprocess={retry} busy={reprocess.isPending} /></div>
            {file.kind === "image" && <div className="mt-6"><ImagePreview fileId={file.id} /></div>}
            <div className="mt-6 flex flex-wrap gap-2">
              {isReady(file) && (
                <button type="button" onClick={() => navigate("/", { state: { attach: [file] } })} className="inline-flex items-center gap-2 rounded-full bg-white px-4 py-2 text-sm font-medium text-ink-950 hover:bg-white/90" data-testid="file-detail-ask">
                  <MessageSquarePlus className="h-4 w-4" /> Ask ChatSleek about this file
                </button>
              )}
              <button type="button" onClick={() => downloadFile(file)} className="inline-flex items-center gap-2 rounded-full border border-white/12 px-4 py-2 text-sm text-zinc-200 hover:bg-white/[0.05]" data-testid="file-detail-download">
                <Download className="h-4 w-4" /> Download
              </button>
            </div>
            {data.preview && (
              <div className="mt-8">
                <h3 className="text-[11px] uppercase tracking-[0.14em] text-zinc-600">What ChatSleek can read</h3>
                <pre className="mt-2 max-h-80 overflow-y-auto whitespace-pre-wrap break-words rounded-xl border border-white/[0.08] bg-ink-850 p-4 font-sans text-[13px] leading-relaxed text-zinc-300" data-testid="file-detail-preview">
                  {data.preview}
                  {data.preview_truncated ? "\n…" : ""}
                </pre>
              </div>
            )}
            {data.conversations?.length > 0 && (
              <div className="mt-8">
                <h3 className="text-[11px] uppercase tracking-[0.14em] text-zinc-600">Used in chats</h3>
                <div className="mt-2 space-y-0.5">
                  {data.conversations.map((c) => (
                    <Link key={c.id} to={`/chat/${c.id}`} onClick={onClose} className="block truncate rounded-lg px-2 py-1.5 text-sm text-zinc-300 hover:bg-white/[0.04] hover:text-white" data-testid={`file-detail-chat-${c.id}`}>
                      {c.title}
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
